// @refresh reload

import Log from "@repo/utils/Log";
import Platform from "@repo/utils-client/Platform";
Log.enabled =
  Platform.isClient &&
  (import.meta.env.APP_ENV !== "production" ||
    new URLSearchParams(window.location.search).has("DEBUG"));

import { createEffect, onMount, Suspense } from "solid-js";
import { MetaProvider, Title } from "@solidjs/meta";
import { Router } from "@solidjs/router";
import { FileRoutes } from "@solidjs/start/router";
import Cookies from "js-cookie";

import { useError } from "@repo/ui/error/ErrorProvider";
import GoogleSignIn from "@repo/ui/external/GoogleSignIn";
import Stripe from "@repo/ui/external/Stripe";
import GlobalRoot from "@repo/ui/global/GlobalRoot";
import Constraints from "@repo/ui/layout/Constraints";
import Masonry from "@repo/ui/layout/Masonry";
import MapLocation from "@repo/ui/location/MapLocation";
import UString from "@repo/utils/UString";
import AppRouter from "@repo/utils-solid/AppRouter";
import USolid from "@repo/utils-solid/USolid";

import { track } from "./analytics/client";
import { features } from "./state/features";
import { useUser } from "./state/features/user";
import { OverlaysProvider } from "./overlays";

import "./app.scss";

export default function App() {
  return (
    <Router
      root={(props) => {
        AppRouter.register();

        if (USolid.runMemo(() => AppRouter.pathname === "/sdk/proxy"))
          return props.children;

        createEffect((from?: string) => {
          track("view page", {
            initial: !from,
            from: from!,
          });
          return AppRouter.pathname;
        });

        return (
          <GoogleSignIn.Provider
            clientId={import.meta.env.GOOGLE_OAUTH_CLIENT_ID}
            apiDomain={UString.afterPrefix(
              new URL(import.meta.env.API_ORIGIN).hostname,
              "api.",
            )}
          >
            <Stripe.Provider
              key={import.meta.env.STRIPE_PUBLIC_KEY}
              theme="dark"
              optionsFor={{
                payment: {
                  fields: {
                    billingDetails: { address: { country: "never" } },
                  },
                },
              }}
            >
              <MapLocation.PropsProvider
                apiKey={import.meta.env.GOOGLE_MAPS_API_KEY}
              >
                <Constraints.PropsProvider maxWidth="1200px">
                  <Masonry.PropsProvider minColumnWidth={400} gap={20} animate>
                    <MetaProvider>
                      <Title>VygorAI</Title>
                      <GlobalRoot
                        OverlaysProvider={OverlaysProvider}
                        featureProviders={features.map(
                          ({ Provider }) => Provider,
                        )}
                      >
                        {(() => {
                          const [, setUser] = useUser();
                          const error = useError();

                          if (Log.enabled) {
                            Object.assign(window, {
                              dev: { AppRouter, Cookies },
                            });
                          }

                          onMount(() => {
                            setUser.fetch().catch((e) => {
                              error.capture(e.message);
                            });
                          });

                          GoogleSignIn.Callback.add(({ credential }) => {
                            setUser.googleSsoCallback(credential).catch((e) => {
                              error.capture(e);
                            });
                          });

                          return <Suspense>{props.children}</Suspense>;
                        })()}
                      </GlobalRoot>
                    </MetaProvider>
                  </Masonry.PropsProvider>
                </Constraints.PropsProvider>
              </MapLocation.PropsProvider>
            </Stripe.Provider>
          </GoogleSignIn.Provider>
        );
      }}
    >
      <FileRoutes />
    </Router>
  );
}
